import styled from '@emotion/styled';
import Button from '@shared/components/core/button/CoreButton';
import TextButton from '@shared/components/core/button/TextButton';
import FieldContainer from '@shared/components/core/field/FieldContainer';
import StyledTextField from '@shared/components/core/field/StyledTextField';
import ChipButtonContainer from '@shared/components/core/layouts/ChipButtonContainer';
import { AttachedMacroCard, MacroWrap } from '@shared/components/core/listing/CommonListingStyles';
import SearchField from '@shared/components/core/search/SearchField';
import { FieldLabel } from '@shared/components/core/text/FormsTypography';
import CommandField from '@shared/components/input/field/CommandField';
import CommandPickerDialog from '@shared/components/input/field/CommandPickerDialog';
import MacroExpanderCard from '@shared/components/macro/MacroExpanderCard';
import MacroStepEditor from '@shared/components/macro/field/MacroStepEditor';
import DeprecatedHotkeyDialog from '@shared/components/profile/DeprecatedHotkeyDialog';
import { CommandType } from '@shared/constants/CommandValues';
import { useMacroEditing } from '@shared/hooks/profiles/useMacroEditing';
import { useEditProfileStore } from '@shared/hooks/state/EditProfileContainer';
import useFilterProfileConfigurations from '@shared/hooks/useFilteredProfileConfigurations';
import { useTranslation } from 'react-i18next';

export interface MacroEditorProps {
    /** Ref forwarded from the parent so it can scroll to a selected macro. */
    selectRef?: React.RefObject<HTMLDivElement | null>;
    /** Href used for the "Add macro" button in desktop/profile context.
     *  When omitted, a click handler (`onNewMacro`) is used instead. */
    addMacroHref?: string;
    /** Called when the "Add macro" button is clicked (org context). */
    onNewMacro?: () => void;
    /** Disable interactions (preview mode). */
    disabled?: boolean;
}

/**
 * Renders the full macro list — search bar, expander cards, inline edit form,
 * command picker dialog, and deprecated-hotkey dialog.
 *
 * All state & handlers are sourced from `useMacroEditing()` so the parent only
 * needs to mount this component (and optionally forward a `selectRef`).
 */
export function MacroEditor({ selectRef, addMacroHref, onNewMacro, disabled = false }: MacroEditorProps) {
    const { t } = useTranslation();
    const macros = useEditProfileStore((state) => state.macros);

    const {
        filter,
        macroToAddCommand,
        setMacroToAddCommand,
        editingMacroIndex,
        setEditingMacroIndex,
        handleSearchChange,
        handleClearSearch,
        copyMacro,
        handleRemoveMacro,
        handleMacroChange,
        handleNameChange,
        handleAddStep,
        handleCommandChange,
        handleRemoveCommand,
        handleAddCommand,
        handleAddCommandWithType,
    } = useMacroEditing();

    const { filteredMacros } = useFilterProfileConfigurations({
        profile: useEditProfileStore.getState(),
        filter,
    });

    return (
        <>
            <MacroControlWrap ref={selectRef as React.RefObject<HTMLDivElement>}>
                {macros.length > 1 && (
                    <SearchField value={filter} onChange={handleSearchChange} onClear={handleClearSearch} />
                )}
                <Button
                    id="new-macro-control"
                    $color="dashedAccent"
                    startIcon="plus"
                    disabled={disabled}
                    to="./controls/new">
                    {t('common:macros.add')}
                </Button>
            </MacroControlWrap>

            <MacroWrap>
                {filteredMacros?.map((h, i) => (
                    <MacroExpanderCard key={h.id} macro={h} alwaysShowIcons={true} id={h.id}>
                        {editingMacroIndex === i ? (
                            <EditWrapper key={i}>
                                <FieldContainer>
                                    <StyledTextField
                                        fullWidth
                                        disabled={disabled}
                                        label={t('common:macros.name.title')}
                                        value={h.name}
                                        name={`${i}name`}
                                        placeholder={t('common:macros.name.placeholder')}
                                        onChange={(e) => handleNameChange(e.target.value, h)}
                                    />
                                </FieldContainer>

                                <FieldLabel>{t('common:input.titlePlural')}</FieldLabel>
                                {h.commands?.map((c, ci) => (
                                    <CommandField
                                        key={h.id + ci}
                                        value={c.command}
                                        commandType={c.commandType}
                                        onChange={(value) => handleCommandChange(value, h, ci)}
                                        onRemove={() => handleRemoveCommand(c.command, h, ci)}
                                    />
                                ))}
                                <ChipButtonContainer>
                                    <TextButton startIcon="plus" onClick={() => handleAddCommand(h)}>
                                        {t('common:input.addMore')}
                                    </TextButton>
                                </ChipButtonContainer>

                                <FieldLabel>{t('common:event.titlePlural')}</FieldLabel>
                                {h.events?.map((e, ei) => (
                                    <MacroStepEditor
                                        key={ei}
                                        macro={h}
                                        step={e}
                                        stepIndex={ei}
                                        onChange={(macro) => handleMacroChange(macro)}
                                    />
                                ))}
                                <ChipButtonContainer>
                                    <TextButton startIcon="plus" onClick={() => handleAddStep(i)}>
                                        {t('common:event.add')}
                                    </TextButton>
                                </ChipButtonContainer>

                                <ButtonContainer>
                                    <Button
                                        $color="secondary"
                                        $size="small"
                                        startIcon="replicateRows"
                                        onClick={() => copyMacro(h)}>
                                        {t('common:copy')}
                                    </Button>
                                    <Button
                                        $color="secondary"
                                        $size="small"
                                        startIcon="delete"
                                        onClick={() => handleRemoveMacro(h)}>
                                        {t('common:delete')}
                                    </Button>
                                    <Button
                                        $color="primary"
                                        $size="small"
                                        onClick={() => setEditingMacroIndex(undefined)}>
                                        {t('common:finishEdit')}
                                    </Button>
                                </ButtonContainer>
                            </EditWrapper>
                        ) : (
                            <AttachedMacroCard macro={h} showTitle={false} id={h.id}>
                                <ButtonContainer>
                                    <Button
                                        $color="secondary"
                                        $size="small"
                                        startIcon="edit"
                                        onClick={() => setEditingMacroIndex(i)}>
                                        {t('common:edit')}
                                    </Button>
                                    <Button
                                        $color="secondary"
                                        $size="small"
                                        startIcon="replicateRows"
                                        onClick={() => copyMacro(h)}>
                                        {t('common:copy')}
                                    </Button>
                                    <Button
                                        $color="secondary"
                                        $size="small"
                                        startIcon="delete"
                                        onClick={() => handleRemoveMacro(h)}>
                                        {t('common:delete')}
                                    </Button>
                                </ButtonContainer>
                            </AttachedMacroCard>
                        )}
                    </MacroExpanderCard>
                ))}
            </MacroWrap>

            <DeprecatedHotkeyDialog />
            <CommandPickerDialog
                open={!!macroToAddCommand}
                onClose={() => setMacroToAddCommand(undefined)}
                onCommandTypeSelected={(commandType: CommandType) =>
                    handleAddCommandWithType(macroToAddCommand, commandType)
                }
            />
        </>
    );
}

const MacroControlWrap = styled.div`
    flex-basis: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 1rem;
    padding: 1rem 0;
`;

const ButtonContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    gap: 1rem;
    margin: 2rem 0;
`;

const EditWrapper = styled.div`
    box-shadow: none;
    border-top: none;
    border-radius: 10px;
    padding: 1rem;
    width: 100%;
    > * {
        max-width: calc(100% - 2rem);
    }
`;
